import { useCallback } from 'react';
import { toast } from 'sonner';
import { useTaskStore } from '@/stores/taskStore';
import { formatTimeFromDate } from '@/lib/time';
import type { SkipReason } from '@/types';
import { hapticComplete, hapticSkip, hapticTap } from './haptics';
import { fireCompleteRing } from './CompleteRing';
import { UndoToast } from './UndoToast';

export function useTaskAction(): {
  start: (id: string) => void;
  complete: (id: string) => void;
  skip: (id: string, reason: SkipReason) => void;
  remove: (id: string) => void;
  /**
   * One-tap "+15m": a scheduled task is pushed back whole (start+end); an
   * in_progress task keeps its start and gets 15 more minutes on the end.
   */
  snooze: (id: string, minutes?: number) => void;
  /**
   * Un-complete / un-skip — the persistent recovery path once the 5s undo
   * toast has expired. Returns the task to scheduled (or inbox if it never
   * had a slot) and clears the outcome timestamps.
   */
  reopen: (id: string) => void;
} {
  const completeTask = useTaskStore((s) => s.completeTask);
  const skipTask = useTaskStore((s) => s.skipTask);
  const updateTask = useTaskStore((s) => s.updateTask);
  const removeTask = useTaskStore((s) => s.removeTask);
  const restoreTask = useTaskStore((s) => s.restoreTask);
  const setStatus = useTaskStore((s) => s.setStatus);

  // One-tap "Start" — flips the task to in_progress right where the user is,
  // no editor detour. Single focus: any other in_progress task returns to
  // scheduled so the Now card never has two live tasks competing.
  const start = useCallback(
    (id: string) => {
      const tasks = useTaskStore.getState().tasks;
      const target = tasks.find((t) => t.id === id);
      if (!target || target.status === 'in_progress' || target.status === 'done') return;

      for (const t of tasks) {
        if (t.id !== id && t.status === 'in_progress') setStatus(t.id, 'scheduled');
      }
      setStatus(id, 'in_progress');
      void hapticTap();
    },
    [setStatus],
  );

  const complete = useCallback(
    (id: string) => {
      const target = useTaskStore.getState().tasks.find((t) => t.id === id);
      if (!target || target.status === 'done') return;

      const prevStatus = target.status;
      const prevCompletedAt = target.completedAt;

      completeTask(id);
      void hapticComplete();
      fireCompleteRing(id);

      toast.custom(
        (toastId) => (
          <UndoToast
            label={`Marked done: ${target.title}`}
            onUndo={() => {
              updateTask(id, { status: prevStatus, completedAt: prevCompletedAt });
              toast.dismiss(toastId);
            }}
          />
        ),
        { duration: 5000 },
      );
    },
    [completeTask, updateTask],
  );

  const skip = useCallback(
    (id: string, reason: SkipReason) => {
      const target = useTaskStore.getState().tasks.find((t) => t.id === id);
      if (!target || target.status === 'skipped') return;

      const prevStatus = target.status;
      const prevSkippedAt = target.skippedAt;
      const prevSkipReason = target.skipReason;

      skipTask(id, reason);
      void hapticSkip();

      toast.custom(
        (toastId) => (
          <UndoToast
            label={`Skipped: ${target.title}`}
            onUndo={() => {
              updateTask(id, {
                status: prevStatus,
                skippedAt: prevSkippedAt,
                skipReason: prevSkipReason,
              });
              toast.dismiss(toastId);
            }}
          />
        ),
        { duration: 5000 },
      );
    },
    [skipTask, updateTask],
  );

  const snooze = useCallback(
    (id: string, minutes = 15) => {
      const target = useTaskStore.getState().tasks.find((t) => t.id === id);
      if (!target?.scheduledStart || !target.scheduledEnd) return;

      const prevStart = target.scheduledStart;
      const prevEnd = target.scheduledEnd;
      const shiftMs = minutes * 60_000;
      const live = target.status === 'in_progress';

      let label: string;
      if (live) {
        const nextEnd = new Date(prevEnd.getTime() + shiftMs);
        updateTask(id, {
          scheduledEnd: nextEnd,
          durationMinutes: target.durationMinutes + minutes,
        });
        label = `Extended to ${formatTimeFromDate(nextEnd)}: ${target.title}`;
      } else {
        // "Snooze" is relative to NOW for a task that already started (push it
        // out of the current slot), but relative to its own start for a future
        // task (never pull it earlier). Duration is preserved either way.
        const durMs = prevEnd.getTime() - prevStart.getTime();
        const nextStart = new Date(Math.max(prevStart.getTime(), Date.now()) + shiftMs);
        updateTask(id, {
          scheduledStart: nextStart,
          scheduledEnd: new Date(nextStart.getTime() + durMs),
        });
        label = `Snoozed to ${formatTimeFromDate(nextStart)}: ${target.title}`;
      }
      void hapticTap();
      toast.custom(
        (toastId) => (
          <UndoToast
            label={label}
            onUndo={() => {
              updateTask(id, {
                scheduledStart: prevStart,
                scheduledEnd: prevEnd,
                ...(live ? { durationMinutes: target.durationMinutes } : {}),
              });
              toast.dismiss(toastId);
            }}
          />
        ),
        { duration: 5000 },
      );
    },
    [updateTask],
  );

  const remove = useCallback(
    (id: string) => {
      const target = useTaskStore.getState().tasks.find((t) => t.id === id);
      if (!target) return;

      removeTask(id);

      toast.custom(
        (toastId) => (
          <UndoToast
            label={`Deleted: ${target.title}`}
            onUndo={() => {
              restoreTask(target);
              toast.dismiss(toastId);
            }}
          />
        ),
        { duration: 5000 },
      );
    },
    [removeTask, restoreTask],
  );

  const reopen = useCallback(
    (id: string) => {
      const target = useTaskStore.getState().tasks.find((t) => t.id === id);
      if (!target || (target.status !== 'done' && target.status !== 'skipped')) return;

      // undefined-delete semantics on updateTask clear the stale timestamps.
      updateTask(id, {
        status: target.scheduledStart ? 'scheduled' : 'inbox',
        completedAt: undefined,
        skippedAt: undefined,
        skipReason: undefined,
      });
      void hapticTap();
      toast(`Reopened: ${target.title}`);
    },
    [updateTask],
  );

  return { start, complete, skip, remove, snooze, reopen };
}
